/**
 * Замок от копирования и водяной знак — D233, §8.8 ТЗ платформы.
 *
 * Без права копировать страница:
 * - не даёт выделить текст, скопировать, вырезать, открыть меню правой
 *   кнопкой, перетащить картинку и распечатать (печать даёт пустой лист);
 * - кладёт поверх экрана бледный водяной знак «имя · дата и время».
 *
 * **Запретить скриншот веб-страницы нельзя** — экран принадлежит
 * устройству, а не сайту, и фото экрана другим телефоном сделать можно
 * всегда. Поэтому главное здесь — знак: скриншот сделать можно, но на нём
 * написано, кто его сделал и когда. Сторожа выгрузок — на сервере; этот
 * компонент решает, что показать.
 *
 * С правом (владелец, «Собственник», выданное поимённо) — ничего из
 * этого, обычная страница.
 */

import { useEffect, useMemo, useState } from 'react';

const BLOCKED_EVENTS = ['copy', 'cut', 'contextmenu', 'dragstart'] as const;

/** Поля ввода остаются рабочими: запрет — на чтение, а не на работу. */
function inField(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  return Boolean(element?.closest?.('input, textarea, [contenteditable="true"]'));
}

function stamp(now: Date): string {
  return now.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Картинка знака — SVG, повторяемый фоном. Экспорт — ради теста. */
export function watermarkImage(text: string): string {
  const safe = text.replace(/[<>&"]/g, '');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="340" height="200">` +
    `<text x="10" y="120" transform="rotate(-24 170 100)" ` +
    `font-family="sans-serif" font-size="15" fill="rgba(128,128,128,0.16)">${safe}</text>` +
    `</svg>`;
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
}

export function CopyGuard({ allowed, name }: { allowed: boolean; name: string | null }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (allowed) return undefined;
    const block = (event: Event) => {
      if (!inField(event.target)) event.preventDefault();
    };
    for (const kind of BLOCKED_EVENTS) document.addEventListener(kind, block, true);
    document.documentElement.classList.add('no-copy');
    // Время на знаке идёт: скриншот, сделанный через час, должен
    // показывать свой час, а не час открытия страницы.
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => {
      for (const kind of BLOCKED_EVENTS) document.removeEventListener(kind, block, true);
      document.documentElement.classList.remove('no-copy');
      window.clearInterval(timer);
    };
  }, [allowed]);

  const mark = `${name ?? 'Сотрудник'} · ${stamp(now)}`;
  const image = useMemo(() => watermarkImage(mark), [mark]);

  if (allowed) return null;
  return (
    <div
      className="copy-guard"
      data-testid="copy-guard"
      data-mark={mark}
      aria-hidden
      style={{ backgroundImage: image }}
    />
  );
}
